import type {
  ChatMessage,
  UseChatMessagesResult,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useRefs } from '@/scripts/hooks';
import type { UseChatGroupResult } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import useMessageExpireTimer from '@/scripts/hooks/useMessageExpireTimer';
import { useSessionContext } from '@/contexts/session/context';
import { isFileMessage } from '@/contexts/chat/scripts/hooks/useFileMessages/utils';
import { safeJson } from '@/scripts/utils';
import { FileTypeIcon } from '@/configs/options/file';
import type { FileInfo } from '@/contexts/chat/scripts/hooks/useFileMessages/types';
import type { Handlers } from '../messageRenderer';
import { deserializePayloadContent } from '../../../chat-input/helpers';
import FileIcon from './images/file-icon.svg?react';
import styles from './index.module.scss';

interface MessageReplyProps {
  chat: Chat;
  message?: ChatMessage;
  isMe: boolean;
  chatMessages: UseChatMessagesResult;
  chatGroup: UseChatGroupResult;
  handlers?: Handlers;
}

const MessageReply = ({
  chat,
  message,
  isMe,
  chatMessages,
  chatGroup,
  handlers,
}: MessageReplyProps) => {
  const {
    session: { user },
  } = useSessionContext();

  const [linkMessage, setLinkMessage] = useState<ChatMessage | null | undefined>(null);
  const [linkUserName, setLinkUserName] = useState<string>();
  const [replyContent, setReplyContent] = useState<string>();
  const [isDeleted, setIsDeleted] = useState(false);

  const refs = useRefs({
    chat,
    handlers,
    getMessage: chatMessages.getMessage,
    getLinkMessageCount: chatMessages.getLinkMessageCount,
    getMembersByUserId: chatGroup?.getMembersByUserId,
  });

  const isGroup = useMemo(() => chat.category === ChatCategory.GROUP, [chat.category]);

  const { link_msg_id, owner_id, payload_type } = useMemo(() => {
    const data = message?.link_msg_id.split('_');
    return {
      link_msg_id: data?.[0] ?? '',
      owner_id: data?.[1] ?? '',
      payload_type: data?.[2] ?? '',
    };
  }, [message?.link_msg_id]);

  useMessageExpireTimer({
    message: linkMessage!,
    chat,
    onExpire: () => {
      handleOnDeleted();
    },
  });

  const triggerReadUpdate = useMemo(
    () => handlers?.triggerReadArr?.find((item) => item.message_id === link_msg_id),
    [handlers?.triggerReadArr, link_msg_id]
  );

  const triggerAllMessagesUpdate = useMemo(
    () => !handlers?.allMessages?.find((item) => item.message_id === link_msg_id),
    [handlers?.allMessages, link_msg_id]
  );

  const isDestroyed = useMemo(() => linkMessage === undefined, [linkMessage]);
  const info = useMemo(
    () => safeJson(linkMessage?.payload || '', {} as FileInfo),
    [linkMessage?.payload]
  );

  const handleOnDeleted = useCallback(() => {
    setLinkMessage(undefined);
    setIsDeleted(true);
  }, []);

  const linkClick = useCallback(async () => {
    if (linkMessage) {
      const count = await refs.current.getLinkMessageCount(
        chat.chat_id,
        `${linkMessage.message_id}_${owner_id}_${payload_type}`
      );
      if (count > 0) {
        refs.current.handlers?.handleLinkedMsgId(
          linkMessage.id,
          linkMessage.message_id
        );
      } else {
        handleOnDeleted();
      }
    }
  }, [linkMessage, refs, chat.chat_id, owner_id, payload_type, handleOnDeleted]);

  useLayoutEffect(() => {
    if (isGroup) {
      if (owner_id) {
        refs.current
          .getMembersByUserId([{ id: chat.chat_id, args: [owner_id] }])
          .then((members) => {
            const [
              {
                args: [member],
              },
            ] = members;
            setLinkUserName(member?.name);
          });
      }
    } else {
      setLinkUserName(owner_id === user?.userId ? user?.name : chat.name);
    }
  }, [chat.chat_id, chat.name, isGroup, owner_id, refs, user?.name, user?.userId]);

  useLayoutEffect(() => {
    if (link_msg_id || triggerReadUpdate || triggerAllMessagesUpdate) {
      refs.current
        .getMessage(chat.chat_id, { message_id: link_msg_id })
        .then(async (linkMsg) => {
          if (!linkMsg) {
            handleOnDeleted();
            return;
          }
          let replyContent: any = linkMsg.payload;
          if (isFileMessage(linkMsg)) {
            const info = safeJson(linkMsg.payload, {} as any);
            replyContent = info.thumbnail ? 'Photo' : info.name;
          } else {
            replyContent = deserializePayloadContent(linkMsg.payload);
          }
          setReplyContent(replyContent);
          setLinkMessage(linkMsg);
        });
    }
  }, [
    chat.chat_id,
    refs,
    link_msg_id,
    triggerReadUpdate,
    triggerAllMessagesUpdate,
    user?.name,
    handleOnDeleted,
  ]);

  return (
    <div
      className={cls(
        styles.messageReply,
        isMe && styles.isMe,
        linkMessage && styles.canClick,
        isDeleted && styles.isDeleted
      )}
      onClick={linkClick}
    >
      {isFileMessage({ payload_type: payload_type as any }) && (
        <>
          {isDestroyed ? (
            <div className={styles.fileIcon}>
              <FileIcon />
            </div>
          ) : (
            <FileTypeIcon
              width={30}
              height={30}
              style={{ borderRadius: 4 }}
              extType={info.ext}
              src={info.thumbnail}
            />
          )}
        </>
      )}
      <div className={styles.content}>
        <div className={styles.messageReplyTitle}>{linkUserName}</div>
        {isDestroyed ? (
          <div className={styles.messageReplyTip}>Message deleted.</div>
        ) : (
          <div className={styles.messageReplyContent}>{replyContent}</div>
        )}
      </div>
    </div>
  );
};

export default MessageReply;
